import { useEffect, useRef, useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import type { D2DHouse, D2DUnit } from '@/types/d2d';

// Generate a unique session ID for this browser tab/session
const generateSessionId = () => `${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;

interface UseRealtimeSyncProps {
  userId?: string;
  currentStreetId?: string | null;
  currentHouseId?: string | null;
  onHouseInserted?: (house: D2DHouse) => void;
  onHouseUpdated?: (house: D2DHouse) => void;
  onUnitInserted?: (unit: D2DUnit) => void;
  onUnitUpdated?: (unit: D2DUnit) => void;
}

// Track recently created items by this session to avoid duplicates
const recentlyCreatedItems = new Set<string>();

export const addRecentlyCreatedItem = (id: string) => {
  recentlyCreatedItems.add(id);
  // Clean up after 10 seconds
  setTimeout(() => recentlyCreatedItems.delete(id), 10000);
};

export const useRealtimeSync = ({
  userId,
  currentStreetId,
  currentHouseId,
  onHouseInserted,
  onHouseUpdated,
  onUnitInserted,
  onUnitUpdated,
}: UseRealtimeSyncProps) => {
  const channelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);
  const sessionId = useMemo(() => generateSessionId(), []);

  useEffect(() => {
    if (!userId) return;

    // Clean up previous channel
    if (channelRef.current) {
      supabase.removeChannel(channelRef.current);
    }

    const channel = supabase.channel('d2d-realtime-sync');

    // Listen for house changes when viewing a street
    if (currentStreetId) {
      channel.on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'd2d_houses',
          filter: `street_id=eq.${currentStreetId}`,
        },
        (payload) => {
          const newHouse = payload.new as D2DHouse;
          
          // Skip if we just created this item in this session
          if (recentlyCreatedItems.has(newHouse.id)) {
            console.log('🏠 Skipping house we just created:', newHouse.id);
            return;
          }
          
          // Show toast only if created by another user
          if (newHouse.user_id !== userId) {
            console.log('🏠 New house created by another user:', newHouse);
            toast({
              title: "Neue Hausnummer",
              description: `Ein Teammitglied hat Hausnummer ${newHouse.house_number} erstellt.`,
            });
          } else {
            console.log('🏠 New house from same user, different device:', newHouse);
          }
          
          onHouseInserted?.(newHouse);
        }
      );

      channel.on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'd2d_houses',
          filter: `street_id=eq.${currentStreetId}`,
        },
        (payload) => {
          const updatedHouse = payload.new as D2DHouse;
          
          // Skip if we just updated this item in this session
          if (recentlyCreatedItems.has(`update-${updatedHouse.id}`)) {
            return;
          }
          
          // Always sync updates for collaborative editing
          if (updatedHouse.user_id !== userId) {
            console.log('🏠 House updated by another user:', updatedHouse);
          }
          onHouseUpdated?.(updatedHouse);
        }
      );
    }

    // Listen for unit changes when viewing a house
    if (currentHouseId) {
      channel.on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'd2d_units',
          filter: `house_id=eq.${currentHouseId}`,
        },
        (payload) => {
          const newUnit = payload.new as D2DUnit;
          
          // Skip if we just created this item in this session
          if (recentlyCreatedItems.has(newUnit.id)) {
            console.log('🏢 Skipping unit we just created:', newUnit.id);
            return;
          }
          
          // Show toast only if created by another user
          if (newUnit.user_id !== userId) {
            console.log('🏢 New unit created by another user:', newUnit);
            toast({
              title: "Neue Wohneinheit",
              description: `Ein Teammitglied hat ${newUnit.unit_name || 'eine neue Einheit'} erstellt.`,
            });
          } else {
            console.log('🏢 New unit from same user, different device:', newUnit);
          }
          
          onUnitInserted?.(newUnit);
        }
      );

      channel.on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'd2d_units',
          filter: `house_id=eq.${currentHouseId}`,
        },
        (payload) => {
          const updatedUnit = payload.new as D2DUnit;
          
          // Skip if we just updated this item in this session
          if (recentlyCreatedItems.has(`update-${updatedUnit.id}`)) {
            return;
          }
          
          // Always sync updates for collaborative editing
          if (updatedUnit.user_id !== userId) {
            console.log('🏢 Unit updated by another user:', updatedUnit);
          }
          onUnitUpdated?.(updatedUnit);
        }
      );
    }

    channel.subscribe((status) => {
      if (status === 'SUBSCRIBED') {
        console.log('✅ Realtime sync channel subscribed');
      }
    });

    channelRef.current = channel;

    return () => {
      if (channelRef.current) {
        console.log('🔌 Cleaning up realtime sync channel');
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
    };
  }, [userId, currentStreetId, currentHouseId, onHouseInserted, onHouseUpdated, onUnitInserted, onUnitUpdated, sessionId]);
};
